import { SocialIcons } from '@/components/icons/SocialIcons';
import { Button } from '@/lib';
import { BaseModal } from '@/lib/Modals';
import { cn } from '@/lib/utils';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuCheck } from 'react-icons/lu';
import { ComuniChannel } from '../../templates/catalog';
import { CHANNEL_LABELS } from './ChannelIndicator';

const CHANNEL_OPTIONS: { channel: ComuniChannel; hintKey: string }[] = [
  { channel: 'whatsapp', hintKey: 'modelli.editor.channelHintWhatsapp' },
  { channel: 'sms', hintKey: 'modelli.editor.channelHintSms' },
  { channel: 'email', hintKey: 'modelli.editor.channelHintEmail' },
];

type NewModelChannelDialogProps = {
  onClose: () => void;
  onConfirm: (channel: ComuniChannel) => void;
};

export const NewModelChannelDialog: FC<NewModelChannelDialogProps> = ({ onClose, onConfirm }) => {
  const { t } = useTranslation('comuni');
  const [selected, setSelected] = useState<ComuniChannel | null>(null);

  return (
    <BaseModal onClose={onClose} classNames={{ dialog: 'max-w-4xl w-full', content: 'min-h-0 p-0' }}>
      <div className='p-6 sm:p-8'>
        <h2 className='text-[22px] font-semibold tracking-tight'>{t('modelli.editor.chooseChannel')}</h2>

        <div className='mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3'>
          {CHANNEL_OPTIONS.map(({ channel, hintKey }) => {
            const isSelected = selected === channel;
            return (
              <button
                key={channel}
                type='button'
                aria-pressed={isSelected}
                onClick={() => setSelected(channel)}
                className={cn(
                  'relative flex flex-col items-center gap-2 rounded-[14px] border p-5 text-center',
                  isSelected ? 'border-2 border-brand-600 bg-brand-50 p-[19px]' : 'bg-white-100 hover:border-brand-200'
                )}
              >
                {isSelected ? (
                  <span className='absolute right-3 top-3 flex size-[22px] items-center justify-center rounded-full bg-brand-700'>
                    <LuCheck className='size-3 text-white-100' strokeWidth={3} />
                  </span>
                ) : null}
                <SocialIcons type={channel} className='mb-1.5 flex size-11 items-center justify-center' iconClassName='size-6' />
                <span className='text-[15px] font-semibold leading-snug'>{CHANNEL_LABELS[channel]}</span>
                <span className='text-xs leading-relaxed font-normal text-slate-400'>{t(hintKey)}</span>
              </button>
            );
          })}
        </div>

        <div className='mt-6 flex justify-end gap-3'>
          <Button intent='secondary' size='md' onClick={onClose} text={t('composer.saveCancel')} />
          <Button intent='primary' size='md' disabled={!selected} onClick={() => selected && onConfirm(selected)} text={t('modelli.editor.continue')} />
        </div>
      </div>
    </BaseModal>
  );
};
